/**
 * Main Game State Types
 * Based on game_model.yaml
 */

import { PlayerState, NPCState } from './Player';
import { InventoryItem } from './Inventory';
import { TeamDecision, QuestionId } from './Question';

export type StageId = 0 | 1 | 2 | 3 | 4 | 5;
export type StageVariant = 'A' | 'B' | null;

export interface Stage {
  id: StageId;
  name: string;
  location: string;
  variant?: StageVariant;
  steps: StageStep[];
}

export interface StageStep {
  step: string;
  name: string;
  questions: QuestionId[];
  requiresConsensus?: boolean;
  consensusField?: string;
  individual?: boolean;
  conditionalNarratives?: boolean;
}

export interface GameFlags {
  // Stage 5 variant determination
  stage5Variant: 'A' | 'B';

  // Conditional flags
  fuelLeakDiscovered: boolean;
  diverPoisoned: boolean;
  containerLost: boolean;

  // Q9 outcomes
  rainbowRayCaptured: boolean;
  cleanupPartial: boolean;
  cleanupFull: boolean;
  goldenOctopusCaptured: boolean;

  // Ending flags
  missionComplete: boolean;
  allDead: boolean;
}

export interface GameState {
  // Session info
  sessionId: string;
  createdAt: Date;
  updatedAt: Date;

  // Current stage
  currentStage: StageId;
  currentStep: string;
  currentVariant: StageVariant;

  // Hunting permit tracking
  huntingPermitDay: number; // 0-6

  // Players
  players: PlayerState[];
  npcs: NPCState[];

  // Team state
  teamName: string;
  teamEnergyPacks: number; // Default: 37 (7 chars × 5 packs + 2 extra)
  inventory: InventoryItem[];

  // Decisions history
  decisions: TeamDecision[];

  // Game flags
  flags: GameFlags;

  // Game status
  gameStatus: 'waiting' | 'in_progress' | 'completed' | 'failed';
}

/**
 * Stage definitions from game_model.yaml
 */
export const STAGES: Record<StageId, Stage> = {
  0: {
    id: 0,
    name: 'Awakening',
    location: 'USS Follett (Mothership)',
    steps: [
      {
        step: '0.1',
        name: 'Individual Preparation',
        questions: ['Q1', 'Q2'],
        individual: true
      },
      {
        step: '0.2',
        name: 'Team Introduction',
        questions: ['Q3'],
        requiresConsensus: true,
        consensusField: 'team_name'
      },
      {
        step: '0.3',
        name: 'Container Selection',
        questions: ['Q4']
      },
      {
        step: '0.4',
        name: 'Departure Decision',
        questions: ['Q5']
      }
    ]
  },

  1: {
    id: 1,
    name: 'Platform A-51',
    location: 'Ocean Platform A-51',
    steps: [
      {
        step: '1.1',
        name: 'Storm Aftermath',
        questions: ['Q6']
      }
    ]
  },

  2: {
    id: 2,
    name: 'Cave of Noorah',
    location: 'Underwater Cave',
    steps: [
      {
        step: '2.1',
        name: 'Exploration Choice',
        questions: ['Q7'],
        individual: true
      },
      {
        step: '2.2',
        name: 'Evening Debrief',
        questions: [],
        conditionalNarratives: true
      }
    ]
  },

  3: {
    id: 3,
    name: 'Descent',
    location: 'Ocean Depths',
    steps: [
      {
        step: '3.1',
        name: 'Ocean Floor Arrival',
        questions: ['Q8']
      }
    ]
  },

  4: {
    id: 4,
    name: 'The Ray',
    location: 'Ocean Bottom / Debris Field',
    steps: [
      {
        step: '4.1',
        name: 'Activity Selection',
        questions: ['Q9'],
        individual: true
      }
    ]
  },

  5: {
    id: 5,
    name: 'The Ascent',
    location: 'Ascending to Surface',
    steps: [
      {
        step: '5.1',
        name: 'Ascent Decisions',
        questions: [] // Questions determined by variant A or B
      }
    ]
  }
};

/**
 * Stage 5 variant questions
 */
export const STAGE_5_QUESTIONS = {
  A: ['Q10A', 'Q12A', 'Q13A', 'Q14A'] as QuestionId[],
  B: ['Q10B', 'Q11B', 'Q12B', 'Q13B', 'Q14B'] as QuestionId[]
};

/**
 * Ending types
 */
export type EndingType =
  | 'rainbow_ray_captured'
  | 'golden_octopus_safe'
  | 'cleanup_completed'
  | 'black_box_recovered'
  | 'mission_failed'
  | 'golden_octopus_death'
  | 'energy_death';

export interface GameEnding {
  type: EndingType;
  priority: number;
  condition: string;
  result?: 'player_dies' | 'all_players_die';
}

/**
 * Reflection question types
 */
export type ReflectionId = 'R1' | 'R2' | 'R3' | 'R4' | 'R5' | 'R6' | 'R7';

export interface ReflectionQuestion {
  id: ReflectionId;
  question: string;
  type: 'scale' | 'text';
  scale?: number[];
  afterStage: StageId;
}

/**
 * Initialize a new game state
 */
export function createGameState(sessionId: string, players: PlayerState[]): GameState {
  return {
    sessionId,
    createdAt: new Date(),
    updatedAt: new Date(),
    currentStage: 0,
    currentStep: '0.1',
    currentVariant: null,
    huntingPermitDay: 0,
    players,
    npcs: [
      {
        role: 'DOC',
        energy: 100,
        decisions: new Map()
      },
      {
        role: 'GEE',
        energy: 100,
        decisions: new Map()
      }
    ],
    teamName: '',
    teamEnergyPacks: 37,
    inventory: [],
    decisions: [],
    flags: {
      stage5Variant: 'A',
      fuelLeakDiscovered: false,
      diverPoisoned: false,
      containerLost: false,
      rainbowRayCaptured: false,
      cleanupPartial: false,
      cleanupFull: false,
      goldenOctopusCaptured: false,
      missionComplete: false,
      allDead: false
    },
    gameStatus: 'waiting'
  };
}
